import type { FunctionComponent, ReactNode } from 'react';

import {
  CustomDropdown,
  type OptionsT,
  ThemeProvider,
} from '@dr.pogodin/react-utils';

import Avatar from 'components/Avatar';
import type { PublicUserDataT, UserT } from 'utils';

import IconEmail from 'assets/images/icon-email-16px.svg';

import IconPhone from 'assets/images/icon-phone-16px.svg';

import dropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';

import { UserRoles } from '../UserRoles';

import customDropdownTheme from './custom-dropdown.scss';

import S from './style.scss';

type BasePropsT = {
  displayName?: null | string;
  user: Partial<UserT> & PublicUserDataT;
};

type StatusPropsT = {
  setStatus: (status: unknown) => void;
  status: string;
  statusOptions: OptionsT<string>;
};

type PropsT
  = | BasePropsT
    | (BasePropsT & StatusPropsT);

/**
 * Renders the header for user (admin / practitioner) profile, when rendered for
 * an admin user. It contains user avatar, name, email, phone, and optionally
 * a dropdown to view / change user status (which exact meaning depends on
 * the context).
 */
export const UserProfileHeader: FunctionComponent<PropsT> = ({
  displayName,
  user,
  ...rest
}) => {
  let statusPlaceholder: ReactNode;

  if ('status' in rest) {
    const { status } = rest;
    if (status === 'Pending Verification' || status === 'Incomplete Application') {
      statusPlaceholder = <div styleName="statusPlaceholer">{status}</div>;
    }
  }

  const fullName = displayName ?? (
    user.firstName || user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email ?? 'No name'
  );

  return (
    <div className={S.container}>
      <Avatar size={100} user={user} />
      <div styleName="baseInfo">
        <h1>{fullName}</h1>
        {'roles' in user && user.roles ? <UserRoles roles={user.roles} /> : null}
        <div styleName="row">
          <IconEmail /> {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            user.email || 'not provided'
          }
        </div>
        <div styleName="row">
          <IconPhone /> {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            user.phone || 'not provided'
          }
        </div>
      </div>
      {'status' in rest ? (
        <div styleName="statusWrapper">
          <ThemeProvider themes={{ CustomDropdown: dropdownTheme }}>
            <CustomDropdown
              label="Status"
              onChange={rest.setStatus}
              options={rest.statusOptions}
              theme={customDropdownTheme}
              value={rest.status}
            />
            {statusPlaceholder}
          </ThemeProvider>
        </div>
      ) : null}
    </div>
  );
};
